import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as service from '../../services/bauordnungService';
import type { SchreibKontext } from '../../services/bauordnungService';
import type { BauordnungBundle } from './types';

/**
 * Der Hook des Moduls 06: Laden, Schreiben, neu laden.
 *
 * ═══ NACH JEDEM SCHREIBEN NEU LADEN ═══════════════════════════════════════
 * Der Hook schreibt nicht in den lokalen Zustand hinein, sondern laedt das
 * Buendel neu. Das ist eine Umdrehung mehr, aber es gibt keinen zweiten Ort,
 * an dem der Stand auseinanderlaufen kann — und genau das ist in diesem Repo
 * schon mehrfach die Ursache stiller Fehler gewesen.
 *
 * ═══ FEHLER WERDEN NICHT VERSCHLUCKT ══════════════════════════════════════
 * Ein stillschweigend fehlgeschlagener Schreibvorgang ist die schlechteste
 * aller Rueckmeldungen: der Anwender tippt weiter und merkt es erst, wenn
 * nichts mehr da ist.
 */

export interface BauordnungLage {
    bundle: BauordnungBundle | null;
    laedt: boolean;
    fehler: string | null;
}

const LEER: BauordnungBundle = {
    ordnung: null, abschnitte: [], regeln: [], rollen: [], luecken: [], eigeneKapitel: [],
};

export const useBauordnung = (projectId: string, nurAnsicht: boolean, handelnder?: string) => {
    const [lage, setLage] = useState<BauordnungLage>({ bundle: null, laedt: true, fehler: null });
    const [arbeitet, setArbeitet] = useState(false);

    /**
     * Das Projekt, das gerade offen ist — `null` nach dem Aushaengen. Ein
     * Schreibvorgang, der erst nach einem Projektwechsel zu Ende geht (der
     * Freigabe-Lauf laeuft weiter, `useFreigabeLauf`), laedt SEIN Projekt
     * nach; dessen Buendel und Fehler duerfen dann nicht beim offenen stehen.
     */
    const offenesProjekt = useRef<string | null>(projectId);
    useEffect(() => {
        offenesProjekt.current = projectId;
        return () => { offenesProjekt.current = null; };
    }, [projectId]);

    /**
     * `still` laedt nach, ohne den Ladezustand zu setzen: der Ladezustand
     * blendet im Modul die Reiter aus, und ein Reiter, der nach jedem
     * Schreiben kurz verschwindet, verliert den Fokus des Eingabefeldes.
     */
    const laden = useCallback(async (still = false) => {
        const offen = () => offenesProjekt.current === projectId;
        if (!offen()) return;
        setLage(l => ({ ...l, laedt: still ? l.laedt : true, fehler: null }));
        try {
            const bundle = await service.fetchBauordnungBundle(projectId);
            if (offen()) setLage({ bundle, laedt: false, fehler: null });
        } catch (e) {
            if (offen()) setLage({ bundle: { ...LEER }, laedt: false, fehler: (e as Error).message });
        }
    }, [projectId]);

    useEffect(() => { void laden(); }, [laden]);

    /** Der Schreibkontext, wie ihn jeder schreibende Aufruf verlangt. */
    const ctx = useMemo<SchreibKontext>(() => ({
        nurAnsicht,
        status: lage.bundle?.ordnung?.status,
        handelnder,
    }), [nurAnsicht, lage.bundle?.ordnung?.status, handelnder]);

    const schreiben = useCallback(async <T>(vorgang: () => Promise<T>): Promise<T | null> => {
        setArbeitet(true);
        try {
            const ergebnis = await vorgang();
            await laden(true);
            return ergebnis;
        } catch (e) {
            if (offenesProjekt.current === projectId) setLage(l => ({ ...l, fehler: (e as Error).message }));
            return null;
        } finally {
            setArbeitet(false);
        }
    }, [laden, projectId]);

    return {
        ...lage,
        arbeitet,
        ctx,
        laden,
        schreiben,
        fehlerLoeschen: () => setLage(l => ({ ...l, fehler: null })),
    };
};
